import React, { useRef, useState } from 'react';
import { Upload, X, Loader2, Image as ImageIcon, Link2 } from 'lucide-react';
import { toast } from 'sonner';

const apiBase = () => (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
const MAX_MB = 8;

/**
 * Compact image picker: lets the user EITHER upload a file (preferred) OR
 * paste an external URL. The resolved final URL is passed via onChange().
 *
 * Props:
 *   value      – current image URL (string)
 *   onChange   – (url: string) => void
 *   label      – field label (optional)
 *   testId     – data-testid prefix
 */
export const ImageUploader = ({ value, onChange, label = 'Bild', testId = 'image-uploader' }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [mode, setMode] = useState('upload'); // 'upload' | 'url'

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Nur Bilder erlaubt (JPG, PNG, WEBP, GIF)');
      return;
    }
    if (file.size > MAX_MB * 1024 * 1024) {
      toast.error(`Datei zu groß – max. ${MAX_MB} MB`);
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await fetch(apiBase() + '/api/images/upload', {
        method: 'POST',
        credentials: 'include',
        body: fd,
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d?.detail || 'Upload fehlgeschlagen');
      // Backend returns relative URL like "/api/images/xxx.jpg" – we keep it that way
      // because REACT_APP_BACKEND_URL is appended at render time. But if the consumer
      // wants an absolute URL, we provide one as fallback:
      const url = d.url.startsWith('http') ? d.url : apiBase() + d.url;
      onChange(url);
      toast.success('Bild hochgeladen');
    } catch (e) {
      toast.error(e.message);
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    if (e.dataTransfer.files?.[0]) handleFile(e.dataTransfer.files[0]);
  };

  return (
    <div className="space-y-2" data-testid={testId}>
      {label && <label className="block text-sm font-medium text-gray-300">{label}</label>}

      {/* Mode toggle */}
      <div className="inline-flex items-center bg-black/30 rounded-lg p-1 text-xs">
        <button
          type="button"
          onClick={() => setMode('upload')}
          data-testid={`${testId}-mode-upload`}
          className={`px-3 py-1 rounded-md inline-flex items-center gap-1.5 transition ${
            mode === 'upload' ? 'bg-[#C4A55A] text-black font-semibold' : 'text-gray-400 hover:text-white'
          }`}
        >
          <Upload className="h-3.5 w-3.5" /> Hochladen
        </button>
        <button
          type="button"
          onClick={() => setMode('url')}
          data-testid={`${testId}-mode-url`}
          className={`px-3 py-1 rounded-md inline-flex items-center gap-1.5 transition ${
            mode === 'url' ? 'bg-[#C4A55A] text-black font-semibold' : 'text-gray-400 hover:text-white'
          }`}
        >
          <Link2 className="h-3.5 w-3.5" /> Externer Link
        </button>
      </div>

      {/* Upload mode: dropzone + preview */}
      {mode === 'upload' ? (
        <div
          onDragOver={(e) => e.preventDefault()}
          onDrop={onDrop}
          className="border-2 border-dashed border-[#C4A55A]/30 rounded-lg bg-black/30 p-4 hover:border-[#C4A55A]/60 transition-colors"
        >
          <input
            ref={inputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            className="hidden"
            data-testid={`${testId}-input`}
            onChange={(e) => handleFile(e.target.files?.[0])}
          />

          {value ? (
            <div className="flex items-center gap-3">
              <img
                src={value}
                alt="Vorschau"
                className="h-20 w-32 object-cover rounded border border-white/10 bg-black/60"
              />
              <div className="flex-1 min-w-0">
                <div className="text-xs text-gray-400 truncate">{value}</div>
                <div className="flex items-center gap-2 mt-2">
                  <button
                    type="button"
                    onClick={() => inputRef.current?.click()}
                    disabled={uploading}
                    data-testid={`${testId}-change`}
                    className="text-xs px-2 py-1 rounded-md bg-[#C4A55A]/15 border border-[#C4A55A]/40 text-[#C4A55A] hover:bg-[#C4A55A]/25 inline-flex items-center gap-1"
                  >
                    {uploading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
                    Anderes Bild
                  </button>
                  <button
                    type="button"
                    onClick={() => onChange('')}
                    data-testid={`${testId}-remove`}
                    className="text-xs px-2 py-1 rounded-md bg-red-500/15 border border-red-500/40 text-red-300 hover:bg-red-500/25 inline-flex items-center gap-1"
                  >
                    <X className="h-3 w-3" /> Entfernen
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
              data-testid={`${testId}-pick`}
              className="w-full flex flex-col items-center justify-center gap-2 py-6 text-gray-400 hover:text-white"
            >
              {uploading ? (
                <Loader2 className="h-7 w-7 text-[#C4A55A] animate-spin" />
              ) : (
                <ImageIcon className="h-7 w-7 text-[#C4A55A]/60" />
              )}
              <span className="text-sm">Bild auswählen oder hierher ziehen</span>
              <span className="text-[10px] text-gray-500">JPG, PNG, WEBP, GIF · max. {MAX_MB} MB</span>
            </button>
          )}
        </div>
      ) : (
        // URL mode: text input + preview
        <div className="space-y-2">
          <input
            type="url"
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder="https://…"
            data-testid={`${testId}-url`}
            className="w-full bg-black/60 border border-[#C4A55A]/30 rounded-lg px-3 py-2 text-white focus:border-[#C4A55A] focus:outline-none"
          />
          {value && (
            <img
              src={value}
              alt="Vorschau"
              className="h-24 w-40 object-cover rounded border border-white/10 bg-black/60"
            />
          )}
        </div>
      )}
    </div>
  );
};
